// Translation bank — short Sanskrit sentences paired with a clean English
// rendering, for the `translate` exercise (Sanskrit→English or the reverse, by
// single choice; distractors are other entries' renderings, so exactly one is
// right). Every sentence here is taken from a lesson's own worked examples and
// uses only taught vocabulary and paradigms, so the bank never runs ahead of
// the curriculum. English renderings are pairwise distinct (no two sentences
// share a gloss) to keep both directions unambiguous.

import type { Translation } from './types';

const SENTENCES: Translation[] = [
  // -- noun cases in action (L3–L8 worked sentences) ---------------------------
  { id: 'tr-rama-vana', bucket: 'sentences', deva: 'रामः वनं गच्छति।', iast: 'rāmaḥ vanaṃ gacchati.', en: 'Rāma goes to the forest.' },
  { id: 'tr-nara-grama', bucket: 'sentences', deva: 'नरः ग्रामं गच्छति।', iast: 'naraḥ grāmaṃ gacchati.', en: 'The man goes to the village.' },
  { id: 'tr-sena-grama', bucket: 'sentences', deva: 'सेना ग्रामं रक्षति।', iast: 'senā grāmaṃ rakṣati.', en: 'The army protects the village.' },
  { id: 'tr-nara-phala', bucket: 'sentences', deva: 'नरः फलं खादति।', iast: 'naraḥ phalaṃ khādati.', en: 'The man eats the fruit.' },
  { id: 'tr-vane-phalani', bucket: 'sentences', deva: 'वने फलानि सन्ति।', iast: 'vane phalāni santi.', en: 'There are fruits in the forest.' },
  { id: 'tr-hari-namah', bucket: 'sentences', deva: 'हरये नमः।', iast: 'haraye namaḥ.', en: 'Salutation to Hari.' },
  { id: 'tr-deva-mandira', bucket: 'sentences', deva: 'देवस्य मन्दिरं वने अस्ति।', iast: 'devasya mandiraṃ vane asti.', en: "The god's temple is in the forest." },
  { id: 'tr-guru-phala', bucket: 'sentences', deva: 'शिष्यः गुरवे फलं ददाति।', iast: 'śiṣyaḥ gurave phalaṃ dadāti.', en: 'The disciple gives fruit to the teacher.' },
  { id: 'tr-nadi-samudra', bucket: 'sentences', deva: 'नदी समुद्रं गच्छति।', iast: 'nadī samudraṃ gacchati.', en: 'The river goes to the ocean.' },
  { id: 'tr-deva-mala', bucket: 'sentences', deva: 'देवः मालया अर्च्यते।', iast: 'devaḥ mālayā arcyate.', en: 'The god is honoured with a garland.' },
  { id: 'tr-nadyam-matsya', bucket: 'sentences', deva: 'नद्यां मत्स्याः सन्ति।', iast: 'nadyāṃ matsyāḥ santi.', en: 'There are fish in the river.' },
  { id: 'tr-guru-devah', bucket: 'sentences', deva: 'गुरुः देवः।', iast: 'guruḥ devaḥ.', en: 'The teacher is a god.' },

  // -- verb / agreement (L9–L10 worked sentences) ------------------------------
  { id: 'tr-aham-gam', bucket: 'sentences', deva: 'अहं ग्रामं गच्छामि।', iast: 'ahaṃ grāmaṃ gacchāmi.', en: 'I go to the village.' },
  { id: 'tr-tvam-path', bucket: 'sentences', deva: 'त्वं पठसि।', iast: 'tvaṃ paṭhasi.', en: 'You recite.' },
  { id: 'tr-narau-gam', bucket: 'sentences', deva: 'नरौ वनं गच्छतः।', iast: 'narau vanaṃ gacchataḥ.', en: 'The two men go to the forest.' },
  { id: 'tr-narah-vas', bucket: 'sentences', deva: 'नराः ग्रामे वसन्ति।', iast: 'narāḥ grāme vasanti.', en: 'The men dwell in the village.' },
  { id: 'tr-deva-path', bucket: 'sentences', deva: 'देवः पठति।', iast: 'devaḥ paṭhati.', en: 'The god recites.' },
  { id: 'tr-bala-phala', bucket: 'sentences', deva: 'बालः फलं पश्यति।', iast: 'bālaḥ phalaṃ paśyati.', en: 'The boy sees the fruit.' },

  // -- short imperative / nominal lines (reading L9 punctuated lines) -----------
  { id: 'tr-satyam-vada', bucket: 'sentences', deva: 'सत्यं वद।', iast: 'satyaṃ vada.', en: 'Speak the truth.' },
  { id: 'tr-dharmam-cara', bucket: 'sentences', deva: 'धर्मं चर।', iast: 'dharmaṃ cara.', en: 'Practice dharma.' },
];

const TRANSLATION_SETS: Record<string, Translation[]> = {
  sentences: SENTENCES,
  // Noun-case focused subset (no finite-verb-agreement sentences).
  nouns: SENTENCES.filter((s) =>
    ['tr-rama-vana', 'tr-nara-grama', 'tr-sena-grama', 'tr-nara-phala', 'tr-vane-phalani', 'tr-hari-namah', 'tr-deva-mandira', 'tr-guru-phala', 'tr-nadi-samudra', 'tr-deva-mala', 'tr-nadyam-matsya', 'tr-guru-devah'].includes(s.id),
  ),
  // Verb / agreement focused subset.
  verbs: SENTENCES.filter((s) =>
    ['tr-aham-gam', 'tr-tvam-path', 'tr-narau-gam', 'tr-narah-vas', 'tr-deva-path', 'tr-bala-phala'].includes(s.id),
  ),
};

export function translations(key: string): Translation[] {
  return TRANSLATION_SETS[key] ?? [];
}
